import React, { useState, useEffect } from "react";
import MemoryGameMetas from "../metas/memorygame";
import userWon from "../components/game_record";
import "../assets/css/common_margin.css";
import { trackButtonClick } from "./event_track";

const MemoryGame = () => {
  const [cards, setCards] = useState([]);
  const [initialReveal, setInitialReveal] = useState(true);
  const [selectedCardValue, setSelectedCardValue] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [matchedCards, setMatchedCards] = useState([]);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [remainingTime, setRemainingTime] = useState(10);
  const [userName, setUserName] = useState("");
  const [centerEffect, setCenterEffect] = useState(null);

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName") || "";
    setUserName(storedUserName);

    const numbers = Array.from({ length: 9 }, (_, index) => index + 1);
    const duplicatedNumbers = [...numbers, ...numbers];
    const shuffledNumbers = duplicatedNumbers.sort(() => Math.random() - 0.5);
    setCards(shuffledNumbers);
  }, []);

  useEffect(() => {
    if (initialReveal) {
      const timer = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer);
            setInitialReveal(false);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [initialReveal]);

  const handleCardClick = (index) => {
    if (initialReveal || gameCompleted || matchedCards.includes(index)) return;

    if (selectedCardIndex === index) {
      setSelectedCardValue(null);
      setSelectedCardIndex(null);
      return;
    }

    if (selectedCardValue === null) {
      setSelectedCardValue(cards[index]);
      setSelectedCardIndex(index);
    } else {
      if (cards[index] === selectedCardValue && index !== selectedCardIndex) {
        setMatchedCards((prev) => [...prev, index, selectedCardIndex]);

        setCenterEffect(cards[index]);

        setTimeout(() => {
          setCenterEffect(null);
          setSelectedCardValue(null);
          setSelectedCardIndex(null);
        }, 1000);

        if (matchedCards.length + 2 === cards.length) {
          setGameCompleted(true);
          userWon(userName, "other");
        }
      } else {
        setSelectedCardValue(cards[index]);
        setSelectedCardIndex(index);
      }
    }
  };

  const cardStyle = {
    width: "90px",
    height: "90px",
    backgroundColor: "#ccc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    float: "left",
    margin: "10px",
    overflow: "hidden",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  const flippedCardStyle = {
    ...cardStyle,
    backgroundColor: "#fff",
  };

  const handleRestart = () => {
    trackButtonClick("Memory game page");

    const numbers = Array.from({ length: 9 }, (_, index) => index + 1);
    const duplicatedNumbers = [...numbers, ...numbers];
    const shuffledNumbers = duplicatedNumbers.sort(() => Math.random() - 0.5);
    setCards(shuffledNumbers);

    setInitialReveal(true);
    setRemainingTime(10);
    setSelectedCardValue(null);
    setSelectedCardIndex(null);
    setMatchedCards([]);
    setGameCompleted(false);
  };

  const fetchImageAsBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  useEffect(() => {
    const preloadAndStoreImages = async () => {
      const values = Array.from({ length: 9 }, (_, index) => index + 1);
      for (const value of values) {
        const key = `flower-${value}`;
        if (!localStorage.getItem(key)) {
          const base64 = await fetchImageAsBase64(
            `/assets/img/flowers/${value}.webp`
          );
          localStorage.setItem(key, base64);
        }
      }
    };
    preloadAndStoreImages();
  }, []);

  return (
    <>
      <MemoryGameMetas />
      <main id="main">
        <div className="breadcrumbs" data-aos="fade-in">
          <div className="container">
            <h2>Memory Game</h2>
            <p>
              Go ahead and remember all the values. You have only 10 seconds to
              memorize them! Find the matching pair of images.
            </p>
          </div>
        </div>
        <section id="memoryGame" className="memoryGame">
          <div className="container" data-aos="fade-up">
            <div className="row mt-5">
              <div className="memory-game-container">
                <h2 className="game-title">
                  {gameCompleted && (
                    <button
                      className="restart-button btn btn-success"
                      onClick={handleRestart}
                    >
                      Restart
                    </button>
                  )}
                  <p>
                    {remainingTime > 0
                      ? `Time Remaining: ${remainingTime}s`
                      : ""}
                  </p>
                </h2>
                <div className="grid">
                  {cards.map((value, index) => (
                    <div
                      key={index}
                      className={`card ${
                        matchedCards.includes(index) ? "matched" : ""
                      }`}
                      onClick={() => handleCardClick(index)}
                      style={
                        initialReveal || matchedCards.includes(index)
                          ? flippedCardStyle
                          : cardStyle
                      }
                    >
                      {(initialReveal ||
                        matchedCards.includes(index) ||
                        selectedCardIndex === index) && (
                        <img
                          src={localStorage.getItem(`flower-${value}`)}
                          alt={`Flower ${value}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </div>
                  ))}
                  {centerEffect !== null && (
                    <div className="center-effect">{centerEffect}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default MemoryGame;
